import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { portal, Application, PortalNotification, ReviewFeedback } from '../lib/portal';
import type { StepAccessEntry } from '../lib/portal';
import { pickActiveApplication } from '../lib/activeApplication';
import {
  CompletionStage,
  NormalStep,
  REQUIRED_REFERENCES,
  completionStage,
  countSubmittedReferees,
  isDone,
  nonReferenceComplete,
  normaliseSteps,
  progress,
} from '../lib/steps';

export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]): {
  data: T | undefined;
  error: string | undefined;
  loading: boolean;
  reload: () => void;
} {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const silentRef = useRef(false);

  useEffect(() => {
    let alive = true;
    // reload() refreshes IN PLACE: the previous data stays rendered while the
    // refetch runs. Flipping loading here unmounted the whole page into the
    // skeleton on every post-save steps reload, destroying the "Saved."
    // banner before the user could see it. Deps changes (e.g. a different
    // application) still show the skeleton.
    if (!silentRef.current) setLoading(true);
    silentRef.current = false;
    fn()
      .then((d) => alive && (setData(d), setError(undefined)))
      .catch((e) => alive && setError((e as Error).message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  const reload = useCallback(() => {
    silentRef.current = true;
    setTick((t) => t + 1);
  }, []);
  return { data, error, loading, reload };
}

/** The caller's applications; `active` = newest non-terminal one.
 *
 * Terminality comes from `stageIsTerminal` (stage-definition-derived): the
 * row's `status` is Globus's READINESS state (on_track / at_risk / …), which
 * never takes lifecycle values like "withdrawn" — the status list stays only
 * as a defensive extra. Among non-terminal rows the newest (appUpdatedAt)
 * wins; previously the OLDEST row was picked whenever several were active,
 * which surfaced an empty stale application instead of the live one. Keep in
 * rule-lockstep with the server's pickActiveApplication (context.controller). */
export function useApplications() {
  // react-query (shared cache) instead of useAsync: AppLayout and every page
  // call this hook independently — the cache dedupes them into ONE network
  // fetch instead of one per mount, and back-navigations render instantly.
  const q = useQuery({ queryKey: ['applications'], queryFn: () => portal.applications() });
  const state = {
    data: q.data,
    error: q.error ? (q.error as Error).message : undefined,
    loading: q.isPending,
    reload: () => void q.refetch(),
  };
  const apps = state.data ?? [];
  const active = pickActiveApplication(apps);
  return { ...state, apps, active };
}

/** Lean journey rows (no fieldsConfig/stepData), shared between the shell
 *  progress banner, the dashboard and check-ins — one cached fetch, not one
 *  per surface. */
export function useJourney(appId: string | number | undefined) {
  const q = useQuery({
    queryKey: ['journey', String(appId)],
    queryFn: () => portal.journey(appId!),
    enabled: appId != null,
  });
  return {
    data: q.data,
    error: q.error ? (q.error as Error).message : undefined,
    loading: appId != null && q.isPending,
    reload: () => void q.refetch(),
  };
}

/** Applicant's reference rows. Gated: only fetched when the caller actually
 *  needs them (deciding whether references are the ONLY thing outstanding), so
 *  most page loads never hit this endpoint. Shared cache key → the shell and
 *  the dashboard dedupe into one fetch. */
export function useReferences(appId: string | number | undefined, enabled = true) {
  const q = useQuery({
    queryKey: ['references', String(appId)],
    queryFn: () => portal.references(appId!),
    enabled: appId != null && enabled,
  });
  return {
    data: q.data,
    error: q.error ? (q.error as Error).message : undefined,
    loading: appId != null && enabled && q.isPending,
    reload: () => void q.refetch(),
  };
}

/** Two-stage application-completion signal (see steps.ts completionStage),
 *  shared by the shell banner, the one-time celebration toast and the dashboard
 *  success card. The reference fetch is gated on "everything else is done" so it
 *  stays free for everyone still mid-application. */
export function useCompletion(appId: string | number | undefined): {
  steps: NormalStep[];
  done: number;
  total: number;
  pct: number;
  nextStep?: NormalStep;
  stage: CompletionStage;
  loading: boolean;
} {
  const { data: rawSteps, loading } = useJourney(appId);
  const steps = useMemo<NormalStep[]>(() => (rawSteps ? normaliseSteps(rawSteps) : []), [rawSteps]);
  const nonRefComplete = useMemo(() => nonReferenceComplete(steps), [steps]);
  const { data: refRows } = useReferences(appId, nonRefComplete);
  const refereesSubmitted = countSubmittedReferees(refRows ?? []) >= REQUIRED_REFERENCES;
  const total = steps.length;
  const done = steps.filter(isDone).length;
  const pct = total ? progress(steps).pct : 0;
  const nextStep = steps.find((s) => !isDone(s));
  const stage = completionStage(steps, refereesSubmitted);
  return { steps, done, total, pct, nextStep, stage, loading };
}

/** Full step rows (fieldsConfig + stepData) for the form page. Cached so a
 *  dashboard prefetch or a back-navigation renders the form without a
 *  skeleton; the post-save path patches single steps into this cache (see
 *  ApplicationPage) instead of refetching the whole payload. */
export function useSteps(appId: string | number | undefined) {
  const q = useQuery({
    queryKey: ['steps', String(appId)],
    queryFn: () => portal.steps(appId!),
    enabled: appId != null,
  });
  return {
    data: q.data,
    error: q.error ? (q.error as Error).message : undefined,
    loading: appId != null && q.isPending,
    reload: () => void q.refetch(),
  };
}

export const identityAnnotationsKey = (appId: string | number) => ['identity-annotations', String(appId)];

/**
 * Applicant-safe identity annotations (passport-authoritative sync). Server
 * state only: the notice text and per-field display copy come from the Hub;
 * the portal never derives "your passport was applied" locally. Enabled only
 * on the steps that show it (Personal / Passport Information). A fetch
 * failure degrades to "no notice" — it must never block the form.
 */
export function useIdentityAnnotations(appId: string | number | undefined, enabled: boolean) {
  const q = useQuery({
    queryKey: identityAnnotationsKey(String(appId)),
    queryFn: () => portal.identityAnnotations(appId!),
    enabled: appId != null && enabled,
    staleTime: 30_000,
    retry: false,
  });
  return q.data;
}

/**
 * Render-ready step for the form page (Asana 1216700496491102). Agreement-
 * marked steps are hydrated from the BFF's single-step endpoint — that route
 * is WRITE-BEARING (agreement version pin on first serve); the canonical
 * placeholder→hydration→pin explanation lives in
 * GlobusService.getApplicationStepConfig. Non-agreement steps pass through
 * untouched with the query disabled — zero extra requests.
 *
 * Contract: `renderStep` is undefined while an agreement step's hydration is
 * pending or failed, so the library's placeholder config can NEVER reach the
 * form engine. `error` carries the failure message; `reload` refetches.
 * `retry: false` overrides the global retry:1 — the route is pin-write-
 * bearing and a doomed retry only doubles the applicant's time-to-error.
 */
export function useHydratedStep(
  step: NormalStep | undefined,
  appId: string | number | undefined,
): { renderStep: NormalStep | undefined; error: string | undefined; reload: () => void } {
  const agreementStepKey =
    step &&
    (step.stepConfig as { fieldsConfig?: { agreement?: { agreementCode?: string } } } | undefined)
      ?.fieldsConfig?.agreement?.agreementCode
      ? step.stepKey
      : undefined;
  const q = useQuery({
    queryKey: ['step', String(appId), agreementStepKey ?? ''],
    queryFn: () => portal.step(appId!, agreementStepKey!),
    enabled: appId != null && !!agreementStepKey,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const hydratedConfig = (q.data as { stepConfig?: unknown } | undefined)?.stepConfig;
  let renderStep: NormalStep | undefined;
  if (step && !agreementStepKey) renderStep = step;
  else if (step && hydratedConfig) renderStep = { ...step, stepConfig: hydratedConfig };
  return {
    renderStep,
    error: agreementStepKey && q.error ? (q.error as Error).message : undefined,
    reload: () => void q.refetch(),
  };
}

/**
 * Sponsor/program-team review feedback for a specific application step.
 * Returns null when no feedback exists (404 normalised to null by portal.reviewFeedback).
 * Disabled when appId or stepKey is not yet known.
 */
export function useReviewFeedback(appId: string | number | undefined, stepKey: string | undefined) {
  const q = useQuery({
    queryKey: ['reviewFeedback', String(appId), stepKey ?? ''],
    queryFn: () => portal.reviewFeedback(appId!, stepKey!),
    enabled: appId != null && !!stepKey,
    staleTime: 30_000,
  });
  return {
    data: q.data as ReviewFeedback | null | undefined,
    loading: appId != null && !!stepKey && q.isPending,
    error: q.error ? (q.error as Error).message : undefined,
    reload: () => void q.refetch(),
  };
}

/**
 * Step-access contract: the server's single authoritative access decision for
 * every step of this application. Fetched with `staleTime: 0` so every mount
 * re-validates; callers must also call `refetch()` after any step submission
 * and on navigation/focus events (see ApplicationPage).
 *
 * The portal is a pure renderer of this contract — it never derives lock
 * state from workflow, journey, or policy data (Nexus section-locking contract).
 */
export function useStepAccess(appId: string | number | undefined) {
  const q = useQuery({
    queryKey: ['step-access', String(appId)],
    queryFn: () => portal.stepAccess(appId!),
    enabled: appId != null,
    staleTime: 0, // always re-validate on mount; access can change at any time
  });
  return {
    data: q.data as StepAccessEntry[] | undefined,
    error: q.error ? (q.error as Error).message : undefined,
    loading: appId != null && q.isPending,
    refetch: () => void q.refetch(),
  };
}

/**
 * Renewal/extension flow availability for the dashboard eligibility card.
 * useQuery (shared cache) instead of useAsync: repeat dashboard mounts render
 * from cache instead of re-hitting the Hub twice per visit. staleTime is
 * 5 minutes because eligibility moves on staff/timeline cadence, not while
 * the applicant navigates; retry:false because the card hides itself on
 * error — a doomed retry only spends the dashboard's network budget.
 * `enabled` gates the fetch off entirely for applicants who could not
 * possibly be eligible; the caller derives that from data it already holds
 * (see DashboardPage's couldBeEligible).
 */
export function useAvailableFlows(appId: string | number | undefined, enabled: boolean) {
  const q = useQuery({
    queryKey: ['availableFlows', String(appId)],
    queryFn: () => portal.availableFlows(appId!),
    enabled: appId != null && enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
  return {
    data: q.data,
    error: q.error ? (q.error as Error).message : undefined,
    loading: appId != null && enabled && q.isPending,
  };
}

/** Outstanding portal_access agreements for the signed-in applicant — backs
 *  TermsGateModal. Not gated on an application existing (account-level). */
export function useRequiredAgreements() {
  const q = useQuery({ queryKey: ['requiredAgreements'], queryFn: () => portal.requiredAgreements() });
  return {
    data: q.data,
    error: q.error ? (q.error as Error).message : undefined,
    loading: q.isPending,
    reload: () => void q.refetch(),
  };
}

/* ---------- notifications (AP-13) ---------- */

/**
 * Paged notification list for the /notifications page.
 * staleTime:0 ensures every page visit re-fetches so newly-delivered
 * notifications appear without a manual refresh.
 */
export function useNotifications(page = 1, pageSize = 20) {
  const q = useQuery({
    queryKey: ['notifications', page, pageSize],
    queryFn: () => portal.notifications(page, pageSize),
    staleTime: 0,
  });
  return {
    data: q.data,
    notifications: q.data?.notifications ?? [],
    total: q.data?.total ?? 0,
    error: q.error ? (q.error as Error).message : undefined,
    loading: q.isPending,
    reload: () => void q.refetch(),
  };
}

/**
 * Unread notification count — lightweight poll for the bell badge.
 * Refreshes every 60 s while the tab is focused; staleTime:0 means a
 * mark-as-read action can immediately invalidate the cache.
 */
export function useNotificationCount() {
  const q = useQuery({
    queryKey: ['notificationsCount'],
    queryFn: () => portal.notificationsCount(),
    staleTime: 0,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
  return {
    unread: q.data?.unread ?? 0,
    loading: q.isPending,
    reload: () => void q.refetch(),
  };
}

/**
 * Mark a single notification as read and invalidate both the count badge
 * and the notifications list cache so all surfaces update immediately.
 */
export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useCallback(
    async (id: string) => {
      await portal.markNotificationRead(id);
      await qc.invalidateQueries({ queryKey: ['notificationsCount'] });
      await qc.invalidateQueries({ queryKey: ['notifications'] });
    },
    [qc],
  );
}

/**
 * Mark all notifications as read and invalidate caches.
 */
export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useCallback(async () => {
    await portal.markAllNotificationsRead();
    await qc.invalidateQueries({ queryKey: ['notificationsCount'] });
    await qc.invalidateQueries({ queryKey: ['notifications'] });
  }, [qc]);
}
